<template>
    <div>
        <v-col cols="12">
            <h2 class="mt-4 mb-0">{{ t('roles::page.title.dms-roles') }}</h2>
            <b-datatable
                :key="datatableKey"
                :columns="columns"
                :endpoint="listRoute"
                has-filter-selector
                :hidden-columns="hiddenColumns"
                item-key="id"
            >
                <template #filters="{ filters }">
                    <v-col cols="4" class="pb-0">
                        <b-filter-input v-model="filters.filters.name" :label="t('roles::label.name')"></b-filter-input>
                    </v-col>
                </template>
                <template #buttons>
                    <div class="px-2">
                        <v-tooltip top transition="v-fab-transition">
                            <template #activator="{ on, attrs }">
                                <v-btn
                                    v-if="can('roles.create')"
                                    :href="createItemUrl()"
                                    color="primary"
                                    icon
                                    v-bind="attrs"
                                    v-on="on"
                                >
                                    <v-icon>mdi-plus-circle</v-icon>
                                </v-btn>
                            </template>
                            <span>{{ t('roles::button.create') }}</span>
                        </v-tooltip>
                    </div>
                </template>

                <template #[`item.active`]="{ item }">
                    <b-confirm
                        v-if="can('api.v1.roles.update') && !item.readonly"
                        :width="400"
                        :hide-true-btn="item.active && item.count_active_users > 0"
                        :text="
                            item.active && item.count_active_users > 0
                                ? t('roles::validation.has_active_users')
                                : item.active
                                ? t('roles::confirmation.deactivation.message')
                                : t('roles::confirmation.activation.message')
                        "
                        :title="
                            item.active && item.count_active_users > 0
                                ? t('roles::confirmation.has_active_users_title')
                                : t('roles::confirmation.title')
                        "
                        @true="updateStatus(item)"
                    >
                        <template #button="{ on }">
                            <v-switch
                                v-model="item.active"
                                :false-value="false"
                                :true-value="true"
                                :readonly="true"
                                v-on="on"
                            >
                            </v-switch>
                        </template>
                    </b-confirm>

                    <div v-else class="grid-status">
                        <span>
                            {{ item.active ? t('default::status.active') : t('default::status.inactive') }}
                        </span>
                    </div>
                </template>

                <template #[`item.group`]="{ item }">
                    {{ t('roles::group.' + item.group) }}
                </template>

                <template #[`item.actions`]="{ item }">
                    <div class="table-actions">
                        <v-tooltip v-if="can('roles.export-record-as-pdf')" top transition="v-fab-transition">
                            <template #activator="{ on, attrs }">
                                <v-btn
                                    color="disabled-color"
                                    icon
                                    v-bind="attrs"
                                    v-on="on"
                                    @click="exportRecordAsPdf(item)"
                                >
                                    <v-icon size="24">mdi-file-export-outline</v-icon>
                                </v-btn>
                            </template>
                            <span>{{ t('default::button.export_record_as_pdf') }}</span>
                        </v-tooltip>

                        <v-tooltip
                            v-if="can('api.v1.roles.update') && !item.readonly"
                            top
                            transition="v-fab-transition"
                        >
                            <template #activator="{ on, attrs }">
                                <v-btn
                                    color="disabled-color"
                                    icon
                                    v-bind="attrs"
                                    v-on="on"
                                    @click="$root.goTo(editItemUrl(item))"
                                >
                                    <v-icon>mdi-pencil</v-icon>
                                </v-btn>
                            </template>
                            <span>{{ t('roles::button.edit') }}</span>
                        </v-tooltip>

                        <v-tooltip v-if="can('api.v1.roles.update') && item.readonly" top transition="v-fab-transition">
                            <template #activator="{ on, attrs }">
                                <v-btn
                                    color="disabled-color"
                                    icon
                                    v-bind="attrs"
                                    v-on="on"
                                    @click="$root.goTo(editItemUrl(item))"
                                >
                                    <v-icon>mdi-eye</v-icon>
                                </v-btn>
                            </template>
                            <span>{{ t('roles::button.preview') }}</span>
                        </v-tooltip>

                        <b-delete
                            v-if="can('api.v1.roles.destroy') && !item.readonly && !item.active"
                            :path="deleteItemUrl(item)"
                            :text="t('default::confirmation.deletion')"
                            :title="t('default::confirmation.general')"
                            icon="mdi-trash-can"
                            @deleted="triggerDeleted"
                        >
                        </b-delete>
                    </div>
                </template>
            </b-datatable>
        </v-col>
    </div>
</template>

<script>
export default {
    props: {
        listRoute: {
            type: String,
            default: null,
        },
    },
    data() {
        return {
            datatableKey: 0,
            downloadingFile: false,
            hiddenColumns: ['id'],
            columns: [
                {
                    text: t('default::grid.label.id'),
                    value: 'id',
                    sortable: false,
                    width: 170,
                },
                {
                    text: t('roles::grid.label.name'),
                    value: 'name',
                    width: 100,
                },
                {
                    text: t('roles::grid.label.group'),
                    value: 'group',
                    width: 100,
                },
                {
                    text: t('roles::grid.label.active'),
                    value: 'active',
                    width: 100,
                    sortable: true,
                },
                {
                    text: t('default::grid.label.actions'),
                    value: 'actions',
                    sortable: false,
                    align: 'end',
                    width: 120,
                    fields: 'readonly,count_active_users',
                },
            ],
        };
    },
    methods: {
        async exportRecordAsPdf(item) {
            if (this.downloadingFile) {
                return;
            }

            this.downloadingFile = true;

            return await axios
                .get(route('roles.export-record-as-pdf', { role: item.id }))
                .then((response) => {
                    const link = document.createElement('a');
                    link.href = response.request.responseURL;
                    document.body.appendChild(link);
                    link.click();
                })
                .catch((error) => {
                    this.$root.notify('error', error.response.data.error);
                })
                .finally(() => {
                    _.delay(() => {
                        this.downloadingFile = false;
                    }, 2000);
                });
        },
        editItemUrl(item) {
            return route('roles.get', { role: item.id });
        },
        createItemUrl() {
            return route('roles.create');
        },
        deleteItemUrl(item) {
            return route('api.v1.roles.destroy', { role: item.id });
        },
        triggerDeleted() {
            this.datatableKey++;
            this.$root.notify('info', t('default::notification.deleted'));
        },
        updateStatus(item) {
            axios({
                method: 'patch',
                url: route('api.v1.roles.update', { role: item.id }),
                data: { active: item.active ? false : true },
            })
                .then((response) => {
                    item.active = response.data.data.active;
                    this.$root.notify('info', t('default::notification.saved'));
                })
                .catch((error) => {
                    if (error.response && error.response.status === 422) {
                        this.$root.notify('error', error.response.data?.errors[0]?.errors[0]);
                    } else {
                        this.$root.notify('error', error.response.data.error);
                    }
                });
        },
    },
};
</script>

<style scoped></style>
